// §5.5 Login OTP — 30s resend, 180s expiry, 3 attempts. The OTP session
// (transactionId + deliveryMethod) arrives in navigation state from /login.
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldHalved,
  faWallet,
  faClock,
} from '@fortawesome/free-solid-svg-icons';
import OtpInput from '@/components/ui/OtpInput/OtpInput';
import { AuthApiError, generateLoginOtp, saveTemporaryPasswordToken, verifyLoginOtp } from '@/api/auth';
import { OTP_EXPIRY_SECONDS, OTP_RESEND_SECONDS } from '@/api/constants';
import { AUTH_LABELS } from '@/constants/static/loginLabels';
import skyline from '../../assets/images/skyline.svg';
import '../Register/Register.css';

const LoginOtpScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as {
    identifier?: string;
    transactionId?: string;
    deliveryMethod?: string;
    maskedDestination?: string;
    expiresInSeconds?: number;
  } | null;

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(OTP_RESEND_SECONDS);
  const [expiresIn, setExpiresIn] = useState(state?.expiresInSeconds ?? OTP_EXPIRY_SECONDS);
  const [session, setSession] = useState({
    transactionId: state?.transactionId ?? '',
    deliveryMethod: state?.deliveryMethod ?? 'SMS',
    maskedDestination: state?.maskedDestination,
  });
  const otpGenerated = useRef(false);

  // Needs a login transaction — direct visits go back to /login
  useEffect(() => {
    if (!state?.identifier) {
      navigate('/login', { replace: true });
      return;
    }
    if (otpGenerated.current || !state.transactionId) return;
    otpGenerated.current = true;
    void generateLoginOtp(session).then((fresh) => {
      setSession({
        transactionId: fresh.transactionId,
        deliveryMethod: fresh.deliveryMethod,
        maskedDestination: fresh.maskedDestination ?? state.maskedDestination,
      });
      setResendIn(OTP_RESEND_SECONDS);
      setExpiresIn(fresh.expiresInSeconds ?? OTP_EXPIRY_SECONDS);
    }).catch((err) => {
      setError(err instanceof AuthApiError ? err.message : 'Could not send the OTP. Please try again.');
    });
  }, [navigate, session, state]);

  useEffect(() => {
    const timer = setInterval(() => {
      setResendIn((s) => (s > 0 ? s - 1 : 0));
      setExpiresIn((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleVerify = async (value: string) => {
    if (loading || !state?.identifier || !session.transactionId || value.length !== 6) return;
    setError('');
    setLoading(true);
    try {
      const result = await verifyLoginOtp(session, value);
      if (result.requiresPasswordChange) {
        if (result.passwordSetupToken) saveTemporaryPasswordToken(result.passwordSetupToken);
        navigate('/set-password', { state: { setupToken: result.passwordSetupToken ?? '', email: state.identifier }, replace: true });
        return;
      }
      if (result.customerState === 'DORMANT' || result.customerState === 'CLOSED') {
        navigate('/welcome', { replace: true, state: { accountUnavailable: result.customerState } });
        return;
      }
      navigate(result.sessionType === 'TRACKING' ? '/applicant' : '/dashboard', { replace: true });
    } catch (err) {
      setCode('');
      setError(
        err instanceof AuthApiError
          ? err.message
          : 'The code you entered is incorrect. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!state?.identifier || resendIn > 0) return;
    try {
      const fresh = await generateLoginOtp(session);
      setSession({ transactionId: fresh.transactionId, deliveryMethod: fresh.deliveryMethod, maskedDestination: fresh.maskedDestination });
      setResendIn(OTP_RESEND_SECONDS);
      setExpiresIn(fresh.expiresInSeconds ?? OTP_EXPIRY_SECONDS);
      setError('');
      setCode('');
    } catch (err) {
      setError(err instanceof AuthApiError ? err.message : 'Could not resend the OTP. Please try again.');
    }
  };

  if (!state?.identifier) return null;

  return (
    <div className="nova-register-page">
      <div className="nova-register-container">
        {/* Left Hero Section — same panel as Welcome / Login */}
        <div className="nova-hero-section">
          <div className="nova-hero-content">
            <div className="nova-logo">
              <div className="nova-logo-mark">{AUTH_LABELS.app.logoMark}</div>
              <span className="nova-logo-text">{AUTH_LABELS.app.name}</span>
            </div>

            <h1 className="nova-hero-title">
              {AUTH_LABELS.hero.titleLine1}
              <br />
              {AUTH_LABELS.hero.titleLine2}
            </h1>

            <div className="nova-feature-cards">
              <div className="nova-feature-card">
                <div className="nova-feature-icon">
                  <FontAwesomeIcon icon={faShieldHalved} />
                </div>
                <div className="nova-feature-body">
                  <h3 className="nova-feature-title">{AUTH_LABELS.hero.featureSecureTitle}</h3>
                  <p className="nova-feature-description">
                    {AUTH_LABELS.hero.featureSecureDescription}
                  </p>
                </div>
              </div>

              <div className="nova-feature-card">
                <div className="nova-feature-icon">
                  <FontAwesomeIcon icon={faWallet} />
                </div>
                <div className="nova-feature-body">
                  <h3 className="nova-feature-title">{AUTH_LABELS.hero.featureManageTitle}</h3>
                  <p className="nova-feature-description">
                    {AUTH_LABELS.hero.featureManageDescription}
                  </p>
                </div>
              </div>

              <div className="nova-feature-card">
                <div className="nova-feature-icon">
                  <FontAwesomeIcon icon={faClock} />
                </div>
                <div className="nova-feature-body">
                  <h3 className="nova-feature-title">{AUTH_LABELS.hero.featureBankingTitle}</h3>
                  <p className="nova-feature-description">
                    {AUTH_LABELS.hero.featureBankingDescription}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <img src={skyline} alt={AUTH_LABELS.hero.skylineAlt} className="nova-skyline" />
        </div>

        {/* Right Section — OTP card */}
        <div className="nova-form-section">
          <div style={{ width: '100%', maxWidth: 600 }}>
            <div className="nova-form-card">
              <button
                type="button"
                aria-label="Back"
                onClick={() => navigate('/login')}
                style={{
                  width: 40,
                  height: 40,
                  border: 'none',
                  borderRadius: 10,
                  background: '#eef2f8',
                  color: '#122b4f',
                  fontSize: '1.1rem',
                  cursor: 'pointer',
                  marginBottom: 24,
                }}
              >
                ←
              </button>

              <div className="text-center mb-4">
                <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#122b4f', marginBottom: 8 }}>
                  Verify login
                </h1>
                <p style={{ color: '#5c6b80', marginBottom: 0 }}>
                  Enter the 6-digit OTP sent to {session.maskedDestination ?? 'your registered contact'}
                </p>
              </div>

              <OtpInput
                id="login-otp"
                value={code}
                onChange={(v) => {
                  setCode(v);
                  setError('');
                  if (v.length === 6) void handleVerify(v);
                }}
                error={error}
                disabled={loading}
              />

              <button
                type="button"
                className="mt-4"
                disabled={loading || code.length !== 6}
                onClick={() => void handleVerify(code)}
                style={{
                  width: '100%',
                  padding: '14px 0',
                  background: 'var(--nova-primary)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: 10,
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  opacity: loading || code.length !== 6 ? 0.6 : 1,
                }}
              >
                {loading && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}
                Verify
              </button>

              <div
                className="d-flex justify-content-between mt-3"
                style={{ fontSize: '0.85rem', color: '#5c6b80' }}
              >
                <button
                  type="button"
                  className="btn btn-link p-0"
                  style={{ fontSize: '0.85rem', color: 'var(--nova-primary)' }}
                  disabled={resendIn > 0}
                  onClick={handleResend}
                >
                  {resendIn > 0 ? `Resend OTP in ${resendIn}s` : 'Resend OTP'}
                </button>
                <span>OTP expires in {expiresIn}s</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginOtpScreen;
